import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { Link as RouterLink } from 'react-router-dom';
import { getKritics } from '../services/kriticsApi';
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Link,
  Divider,
  Tooltip,
  IconButton
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import StarIcon from '@mui/icons-material/Star';
import EmptyState from './EmptyState';
import KriticCard from './KriticCard';
import MatchScoreIndicator from './MatchScoreIndicator';
import KriticIcon from './KriticIcon';

const TopKriticsGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 24px;
`;

const headCells = [
  { id: 'icon', numeric: false, disablePadding: true, label: '' },
  { id: 'name', numeric: false, disablePadding: false, label: 'Kritic Name' },
  { id: 'avg_rating', numeric: true, disablePadding: false, label: 'Avg. Rating' },
  { id: 'match_score', numeric: true, disablePadding: false, label: 'Match Score' },
];

function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={{ fontWeight: 'bold' }}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const Kritics = () => {
  const [kritics, setKritics] = useState([]);
  const [top5Kritics, setTop5Kritics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [maxMatchScore, setMaxMatchScore] = useState(0);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalPages, setTotalPages] = useState(1);

  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('match_score');

  const [filterName, setFilterName] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const user = useMemo(() => JSON.parse(localStorage.getItem('user')), []);

  const fetchKritics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const sort = `${orderBy}:${order}`;
      const data = await getKritics(page, rowsPerPage, sort, filterName, user.id);
      setKritics(data.kritics);
      setTop5Kritics(data.top5Kritics);
      setTotalPages(data.totalPages);
      setMaxMatchScore(data.maxMatchScore);
    } catch (err) {
      setError('Failed to fetch kritics. Ensure you have rated at least 10 movies.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [user.id, page, rowsPerPage, orderBy, order, filterName]);

  useEffect(() => {
    fetchKritics();
  }, [fetchKritics]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(1);
  };

  const handleFilterChange = (event) => {
    setSearchInput(event.target.value);
  };

  const applyFilter = () => {
    setFilterName(searchInput);
    setPage(1);
  };

  const clearAll = () => {
    setOrder('desc');
    setOrderBy('match_score');
    setFilterName('');
    setSearchInput('');
    setPage(1);
    setRowsPerPage(25);
  };

  const renderKriticRow = (kritic, isTop5 = false) => (
    <TableRow
      hover
      key={kritic.site_identifier}
      sx={{
        '&:last-child td, &:last-child th': { border: 0 },
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
        marginBottom: '10px',
        display: 'table-row',
        backgroundColor: isTop5 ? '#fff' : '#ffffff'
      }}
    >
      <TableCell sx={{ padding: '5px' }}>
        <KriticIcon />
      </TableCell>
      <TableCell component="th" scope="row">
        <Link component={RouterLink} to={`/kritics/${kritic.site_identifier}`} sx={{ fontWeight: 'bold' }}>
          {kritic.name}
        </Link>
      </TableCell>
      <TableCell align="right">{kritic.avg_rating.toFixed(2)} / 5.00</TableCell>
      <TableCell align="right">
        <MatchScoreIndicator score={kritic.match_score} maxScore={maxMatchScore} />
      </TableCell>
    </TableRow>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontFamily: '"Carter One", cursive' }}>
        Kritics Match
      </Typography>

      <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          label="Filter by Name"
          variant="outlined"
          value={searchInput}
          onChange={handleFilterChange}
          onKeyPress={(e) => e.key === 'Enter' && applyFilter()}
          size="small"
        />
        <Button variant="contained" onClick={applyFilter}>Filter</Button>
        <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Per Page</InputLabel>
          <Select
            value={rowsPerPage}
            onChange={(e) => { setRowsPerPage(e.target.value); setPage(1); }}
            label="Per Page"
          >
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
          </Select>
        </FormControl>
        <Tooltip title="Clear all sorts and filters">
          <IconButton onClick={clearAll}>
            <ClearAllIcon />
          </IconButton>
        </Tooltip>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}><CircularProgress /></Box>
      ) : error ? (
        <EmptyState message={error} actionText="Go Rate Some Movies" onAction={() => window.location.href = '/'} />
      ) : (
        <>
          {top5Kritics.length > 0 && (
            <Box mb={4}>
              <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontFamily: '"Carter One", cursive' }}>
                <StarIcon sx={{ color: 'gold' }} /> Top 5 Matched Kritics
              </Typography>
              <TopKriticsGrid>
                {top5Kritics.map(kritic => (
                  <KriticCard key={kritic.site_identifier} critic={kritic} maxMatchScore={maxMatchScore} />
                ))}
              </TopKriticsGrid>
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          <TableContainer component={Paper}>
            <Table stickyHeader aria-label="kritics table">
              <EnhancedTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {kritics.map(renderKriticRow)}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2, gap: 2 }}>
            <Button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
              Previous
            </Button>
            <Typography>Page {page} of {totalPages}</Typography>
            <Button onClick={() => setPage(p => p + 1)} disabled={page >= totalPages}>
              Next
            </Button>
          </Box>
        </>
      )}
    </Container>
  );
};

export default Kritics;
